import * as api from "./api";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { Exception } from "sass";
import { getClassroomApiUrl } from "js/utils/getBackendUrl";
import { telemetryHeaders } from "./util";

type AuthHeaders = {
	Authorization: string;
	User: string;
	Device: string;
};

// auth headers for classroom api v1
export function authHeaders(): AuthHeaders {
	const headers = {
		Authorization: useUserSessionStore.getState().getToken() as string,
		User: useUserSessionStore.getState().getActiveUser() as string,
		Device: useUserSessionStore.getState().getActiveDevice() as string,
		"Content-Type": "application/json",
	};

	return headers;
}

const classroomApiUrl = getClassroomApiUrl(window.location.hostname);

export async function Get<TResult>(endpoint: string, version?: "v1" | "v2"): Promise<TResult> {
	//Set version to v1 if none is passed in
	if (!version) {
		version = "v1";
	}

	const options = {
		method: "GET",
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
		},
	};

	try {
		return (await api.fetch(
			`${classroomApiUrl}/${version}/devices/${localStorage.getItem("DEVICE") as string}/${endpoint}`,
			options,
			false
		)) as TResult;
	} catch (error) {
		console.log(error);
		return null as TResult;
	}
}

// like Get(), but doesn't prepend the version or devices to the url - since not all endpoints have this prefix now
export async function Get2<TResult>(endpoint: string): Promise<TResult> {
	const options = {
		method: "GET",
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
		},
	};

	try {
		return (await api.fetch(`${classroomApiUrl}/${endpoint}`, options, false)) as TResult;
	} catch (error) {
		console.log(error);
		return null as TResult;
	}
}

export async function Post2<TPayload, TResult>(
	endpoint: string,
	payload: TPayload,
	successCallback?: (result: TResult) => void,
	errorCallback?: (result: Exception) => void,
	timeout = 0
): Promise<TResult> {
	const controller = new AbortController();

	const options = {
		method: "POST",
		body: JSON.stringify(payload),
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
		},
		signal: null as AbortSignal | null,
	};

	if (timeout) {
		setTimeout(() => controller.abort(), timeout);
		options.signal = controller.signal;
	}

	try {
		const data = (await api.fetch(`${classroomApiUrl}/${endpoint}`, options, false)) as TResult;
		if (successCallback) {
			successCallback(data);
		} else {
			return data;
		}
	} catch (error) {
		console.log(error);
		if (errorCallback) {
			errorCallback(error as Exception);
		} else {
			throw error;
		}
	}
	return null as TResult;
}

export async function Post<TPayload, TResult>(
	endpoint: string,
	payload: TPayload,
	successCallback?: (result: TResult) => void,
	errorCallback?: (result: Exception) => void,
	timeout = 0
): Promise<TResult> {
	const controller = new AbortController();

	const options = {
		method: "POST",
		body: JSON.stringify(payload),
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
		},
		signal: null as AbortSignal | null,
	};

	if (timeout) {
		setTimeout(() => controller.abort(), timeout);
		options.signal = controller.signal;
	}

	try {
		const data = (await api.fetch(
			`${classroomApiUrl}/v1/devices/${localStorage.getItem("DEVICE") as string}/${endpoint}`,
			options,
			false
		)) as TResult;
		if (successCallback) {
			successCallback(data);
		} else {
			return data;
		}
	} catch (error) {
		console.log(error);
		if (errorCallback) {
			errorCallback(error as Exception);
		} else {
			throw error;
		}
	}
	return null as TResult;
}

/** POST that treats given status codes as success and returns the parsed response body (e.g. 409 for validation conflict). */
export async function PostWithAcceptStatuses<TPayload, TResult>(
	endpoint: string,
	payload: TPayload,
	acceptStatuses: number[]
): Promise<TResult> {
	const options = {
		method: "POST",
		body: JSON.stringify(payload),
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
		},
		acceptStatuses,
	};
	return (await api.fetch(
		`${classroomApiUrl}/v1/devices/${localStorage.getItem("DEVICE") as string}/${endpoint}`,
		options,
		false
	)) as TResult;
}

export async function Put2<TPayload, TResult>(
	endpoint: string,
	payload: TPayload,
	successCallback?: (result: TResult) => void,
	errorCallback?: (result: Exception) => void,
	timeout = 0
): Promise<TResult> {
	const controller = new AbortController();

	const options = {
		method: "PUT",
		body: JSON.stringify(payload),
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
		},
		signal: null as AbortSignal | null,
	};

	if (timeout) {
		setTimeout(() => controller.abort(), timeout);
		options.signal = controller.signal;
	}

	try {
		const data = (await api.fetch(`${classroomApiUrl}/${endpoint}`, options, false)) as TResult;
		if (successCallback) {
			successCallback(data);
		} else {
			return data;
		}
	} catch (error) {
		console.log(error);
		if (errorCallback) {
			errorCallback(error as Exception);
		} else {
			throw error;
		}
	}
	return null as TResult;
}

export async function Put<TPayload, TResult>(
	endpoint: string,
	payload: TPayload,
	successCallback?: (result: TResult) => void,
	errorCallback?: (result: Exception) => void,
	timeout = 0
): Promise<TResult> {
	const controller = new AbortController();

	const options = {
		method: "PUT",
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
		signal: null as AbortSignal | null,
	};

	if (timeout) {
		setTimeout(() => controller.abort(), timeout);
		options.signal = controller.signal;
	}

	try {
		const device = localStorage.getItem("DEVICE") as string;
		const data = (await api.fetch(`${classroomApiUrl}/v1/devices/${device}/${endpoint}`, options, false)) as TResult;
		if (successCallback) {
			successCallback(data);
		} else {
			return data;
		}
	} catch (error) {
		console.log(error);
		if (errorCallback) {
			errorCallback(error as Exception);
		} else {
			throw error;
		}
	}
	return null as TResult;
}

export async function Delete<TPayload, TResult>(
	endpoint: string,
	payload: TPayload,
	successCallback: (result: TResult) => void,
	errorCallback: (result: Exception) => void,
	timeout = 0
): Promise<TResult> {
	const controller = new AbortController();

	const options = {
		method: "DELETE",
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
		signal: null as AbortSignal | null,
	};

	if (timeout) {
		setTimeout(() => controller.abort(), timeout);
		options["signal"] = controller.signal;
	}

	try {
		const data = (await api.fetch(
			`${classroomApiUrl}/v1/devices/${localStorage.getItem("DEVICE") as string}/${endpoint}`,
			options,
			false
		)) as TResult;
		if (successCallback) {
			successCallback(data);
		} else {
			return data;
		}
	} catch (error) {
		console.log(error);
		if (errorCallback) {
			errorCallback(error as Exception);
		} else {
			throw error;
		}
	}
	return null as TResult;
}

export async function DeleteV2<TResult>(
	endpoint: string,
	successCallback?: (result: TResult) => void,
	errorCallback?: (result: Exception) => void,
	timeout = 0
): Promise<TResult> {
	const controller = new AbortController();

	const options = {
		method: "DELETE",
		headers: {
			...authHeaders(),
			...telemetryHeaders(),
		},
		signal: null as AbortSignal | null,
	};

	if (timeout) {
		setTimeout(() => controller.abort(), timeout);
		options["signal"] = controller.signal;
	}

	try {
		const data = (await api.fetch(`${classroomApiUrl}/${endpoint}`, options, false)) as TResult;
		if (successCallback) {
			successCallback(data);
		} else {
			return data;
		}
	} catch (error) {
		console.log(error);
		if (errorCallback) {
			errorCallback(error as Exception);
		} else {
			throw error;
		}
	}
	return null as TResult;
}

export async function fetchWithType<TResult>(endpoint: string, type: string): Promise<TResult> {
	const options = {
		method: "GET",
		headers: {
			...authHeaders(),
		},
	};

	try {
		if (type === "blob") {
			return (await api.fetchBlob(
				`${classroomApiUrl}/v1/devices/${localStorage.getItem("DEVICE") as string}/${endpoint}`,
				options,
				false
			)) as TResult;
		} else if (type === "download") {
			return (await api.fetchDownload(
				`${classroomApiUrl}/v1/devices/${localStorage.getItem("DEVICE") as string}/${endpoint}`,
				options,
				false
			)) as TResult;
		} else {
			console.log("Fetch type not found");
			return null as TResult;
		}
	} catch (error) {
		console.log(error);
		return null as TResult;
	}
}
